import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import AppHeader from '../../components/shell/AppHeader'
import SupervisorNav from '../../components/supervisor/SupervisorNav'
import { useProfileIdentity } from '../../lib/useProfileIdentity'
import EmptyState from '../../components/shell/EmptyState'
import DetailForm, { type DetailFormValues } from '../../components/supervisor/DetailForm'
import TransferForm, { type TransferFormValues } from '../../components/supervisor/TransferForm'
import { supabase } from '../../lib/supabase'
import { effectiveStatusFor, firstDayAfterDetailEnds, type PersonnelStatus } from './personnelDirectory'
import { createDetails, getPersonnelDetails, type PersonnelDetail } from './personnelDetailStore'
import { PAY_PERIOD_20_DATES } from '../agent/payPeriod20'
import { removeEmployeeFromDraft } from './planningBoardDraftStore'
import {
  getPersonnel,
  getTransferDestinations,
  transferEmployees,
  type PersonnelPerson,
  type TransferDestination,
} from './personnelStore'
import { useSupervisorTeams } from './useSupervisorTeams'
import TeamSwitcher from '../../components/supervisor/TeamSwitcher'

/**
 * docs/design/journey-map.md's "Personnel directory": a searchable list of
 * the personnel in scope, filterable by any of name, identifier, title,
 * team, shift, supervisor, qualifications, and language, and by active or
 * detailed status. The team toggle (TeamSwitcher.tsx) is what actually
 * scopes the list to one of Sgt. Diaz's teams, per
 * docs/product/personas.md's "A supervisor owns one or more teams"; search
 * then narrows within it by name, identifier, title, supervisor, or
 * qualification (there is only the one shift, so nothing to search there),
 * and the status filter is its own control because it is the one column
 * that actually varies within a team. A row opens the profile.
 *
 * docs/product/scheduling-model.md's "Details": a checkbox per row selects
 * a set of employees "by team, shift, or search"; the team toggle and the
 * search field cover "team" and "search," and there is still only the one
 * shift to select by. Create a detail applies one set of detail and
 * schedule information to all of them at once, each recorded as its own
 * entry (personnelDetailStore.ts) rather than one shared record, per
 * docs/product/scheduling-model.md's "Bulk reassignment": "Each employee's
 * change is recorded as its own effective-dated assignment." The same
 * selection also drives a bulk transfer (personnelStore.ts's
 * transferEmployees) to any other team, from one effective date: a switch
 * of team clears the selection first, since a bulk action applying to
 * people spread across two different teams at once is not the workflow
 * either capability describes.
 */

const STATUS_FILTERS: { key: PersonnelStatus | 'all'; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'detailed', label: 'Detailed' },
]

function searchableText(person: PersonnelPerson): string {
  return [person.name, person.identifier, person.title, person.team, person.supervisor, ...person.qualifications]
    .join(' ')
    .toLowerCase()
}

export default function SupervisorPersonnelDirectoryPage() {
  const identity = useProfileIdentity()
  const [userId, setUserId] = useState<string | null>(null)
  const [supervisorName, setSupervisorName] = useState<string | null>(null)
  const [sessionReady, setSessionReady] = useState(false)
  const { teams, currentTeamId, teamsLoaded, switchTeam: switchToTeam } = useSupervisorTeams(userId)
  const [people, setPeople] = useState<PersonnelPerson[]>([])
  const [destinations, setDestinations] = useState<TransferDestination[]>([])
  const [details, setDetails] = useState<PersonnelDetail[]>([])
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<PersonnelStatus | 'all'>('all')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [formOpen, setFormOpen] = useState(false)
  const [detailFailed, setDetailFailed] = useState(false)
  const [transferFormOpen, setTransferFormOpen] = useState(false)
  const [transferFailed, setTransferFailed] = useState(false)

  // Waits on the profile fetch too, rather than resolving sessionReady as
  // soon as the session itself does, the same race
  // SupervisorApprovalInboxPage.tsx's own sessionReady closes for
  // supervisorName: by the time the directory is interactive, Create a
  // detail already has a real identity to record it under.
  useEffect(() => {
    let cancelled = false
    supabase.auth
      .getSession()
      .then(async ({ data: { session } }) => {
        if (cancelled) return
        const id = session?.user.id
        if (id) {
          setUserId(id)
          const { data: profile } = await supabase
            .from('profiles')
            .select('first_name, last_name')
            .eq('id', id)
            .single()
          const name = profile ? `${profile.first_name} ${profile.last_name}` : ''
          if (!cancelled && profile) setSupervisorName(name)
          const [loadedPeople, loadedDestinations, loadedDetails] = await Promise.all([
            getPersonnel(id, name),
            getTransferDestinations(),
            getPersonnelDetails(),
          ])
          if (!cancelled) {
            setPeople(loadedPeople)
            setDestinations(loadedDestinations)
            setDetails(loadedDetails)
          }
        }
      })
      .finally(() => {
        if (!cancelled) setSessionReady(true)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const teamName = teams.find((team) => team.id === currentTeamId)?.name ?? ''
  const otherTeams = destinations.filter((team) => team.id !== currentTeamId)
  const query = search.trim().toLowerCase()
  const filtered = people.filter((person) => {
    if (person.teamId !== currentTeamId) return false
    const status = effectiveStatusFor(person, details).status
    if (statusFilter !== 'all' && status !== statusFilter) return false
    if (query && !searchableText(person).includes(query)) return false
    return true
  })

  function switchTeam(nextTeamId: string) {
    if (nextTeamId === currentTeamId) return
    switchToTeam(nextTeamId)
    setSelected(new Set())
    setFormOpen(false)
    setTransferFormOpen(false)
  }

  function toggleSelected(employeeId: string) {
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(employeeId)) next.delete(employeeId)
      else next.add(employeeId)
      return next
    })
  }

  async function saveDetail(values: DetailFormValues) {
    const employeeIds = people.filter((person) => selected.has(person.id)).map((person) => person.id)
    if (employeeIds.length === 0 || employeeIds.length !== selected.size) {
      setDetailFailed(true)
      return
    }
    if (
      !(await createDetails({
        employeeIds,
        detailType: values.type,
        destination: values.destination,
        startDate: values.startDate,
        endDate: values.endDate,
      }))
    ) {
      setDetailFailed(true)
      return
    }
    setDetailFailed(false)
    setDetails(await getPersonnelDetails())
    setSelected(new Set())
    setFormOpen(false)
  }

  async function saveTransfer(values: TransferFormValues) {
    const departing = people.filter((person) => selected.has(person.id))
    const employeeIds = departing.map((person) => person.id)
    if (!userId || employeeIds.length === 0 || employeeIds.length !== selected.size) {
      setTransferFailed(true)
      return
    }
    if (!(await transferEmployees({ employeeIds, ...values }))) {
      setTransferFailed(true)
      return
    }
    setTransferFailed(false)
    // Their unsaved planning board edits on the team they leave name a
    // schedule they no longer keep there.
    for (const person of departing) removeEmployeeFromDraft(userId, person.teamId, person.id)
    // Reloaded rather than patched: a transfer effective later still leaves
    // the person here today, and one effective already moves them off.
    setPeople(await getPersonnel(userId, supervisorName ?? ''))
    setSelected(new Set())
    setTransferFormOpen(false)
  }

  // The latest of every selected, currently detailed person's own
  // earliest transfer date (firstDayAfterDetailEnds), since one effective
  // date applies to the whole bulk transfer and none of them can be
  // scheduled earlier than their own detail allows.
  function minBulkTransferDate(): string | undefined {
    const boundaries = [...selected]
      .map((employeeId) => people.find((candidate) => candidate.id === employeeId))
      .filter((candidate): candidate is PersonnelPerson => candidate !== undefined)
      .map((candidate) => effectiveStatusFor(candidate, details))
      .filter((candidateStatus) => candidateStatus.status === 'detailed')
      .map((candidateStatus) =>
        candidateStatus.detailEndDate ? firstDayAfterDetailEnds(candidateStatus.detailEndDate) : PAY_PERIOD_20_DATES[0]?.date,
      )
      .filter((date): date is string => date !== undefined)
    return boundaries.length === 0 ? undefined : boundaries.reduce((latest, date) => (date > latest ? date : latest))
  }

  if (!sessionReady || (userId && !teamsLoaded)) {
    return (
      <div className="flex h-dvh flex-col bg-surface text-ink">
        <div className="flex flex-1 flex-col items-center justify-center gap-4 p-4">
          <p role="status" className="text-small text-ink-muted">
            Loading&hellip;
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-dvh flex-col bg-surface text-ink">
      <a
        href="#personnel-main"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-2 focus:rounded focus:bg-canvas focus:px-4 focus:py-2 focus:font-semibold"
      >
        Skip to main content
      </a>

      <AppHeader initials={identity?.initials ?? ''} />

      <main id="personnel-main" tabIndex={0} className="flex flex-1 flex-col overflow-y-auto p-4 sm:p-6 lg:p-8">
        <SupervisorNav />
        <div className="mx-auto flex w-full max-w-[1100px] flex-1 flex-col gap-5">
          <div>
            <h1 className="text-h2 font-extrabold">Personnel Directory</h1>
            <p className="mt-1 text-small text-ink-muted">{teamName} &middot; Orlando Field Office</p>
          </div>

          <TeamSwitcher teams={teams} currentTeamId={currentTeamId} onChange={switchTeam} />

          <div className="flex flex-wrap items-center gap-3 rounded-surface border border-line bg-canvas p-3">
            <label htmlFor="personnel-search" className="sr-only">
              Search personnel
            </label>
            <input
              id="personnel-search"
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by name, identifier, title, team, supervisor, or qualification"
              className="min-h-11 min-w-[280px] flex-1 rounded-control border border-line-strong px-3 text-small"
            />
            <div role="group" aria-label="Filter by status" className="flex gap-2">
              {STATUS_FILTERS.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  aria-pressed={statusFilter === option.key}
                  onClick={() => setStatusFilter(option.key)}
                  className={`flex min-h-11 items-center justify-center rounded-control border px-3 text-small font-bold ${
                    statusFilter === option.key ? 'border-primary bg-selected' : 'border-line-strong hover:bg-surface-2'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {selected.size > 0 && (
            <div className="flex flex-wrap items-center gap-3 rounded-surface border border-line bg-canvas p-3">
              <span className="text-small font-bold text-ink-muted">
                {selected.size} {selected.size === 1 ? 'person' : 'people'} selected
              </span>
              <button
                type="button"
                aria-expanded={formOpen}
                onClick={() => setFormOpen((open) => !open)}
                className="flex min-h-11 items-center justify-center rounded-control bg-primary px-4 text-small font-bold text-primary-ink hover:bg-primary-hover"
              >
                Create a detail
              </button>
              {otherTeams.length > 0 && (
                <button
                  type="button"
                  aria-expanded={transferFormOpen}
                  onClick={() => setTransferFormOpen((open) => !open)}
                  className="flex min-h-11 items-center justify-center rounded-control border border-line-strong px-4 text-small font-bold hover:bg-surface-2"
                >
                  Transfer
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setSelected(new Set())
                  setFormOpen(false)
                  setTransferFormOpen(false)
                }}
                className="flex min-h-11 items-center justify-center rounded-control px-3 text-small font-bold text-ink-muted hover:bg-surface-2"
              >
                Clear selection
              </button>
            </div>
          )}

          {detailFailed && (
            <p role="alert" className="text-caption font-semibold text-error">
              Could not create the detail. Check the dates; an employee cannot hold two details that overlap.
            </p>
          )}

          {transferFailed && (
            <p role="alert" className="text-caption font-semibold text-error">
              Could not make the transfer. Check the date and try again.
            </p>
          )}

          {formOpen && <DetailForm onSave={saveDetail} onCancel={() => setFormOpen(false)} />}

          {transferFormOpen && otherTeams.length > 0 && (
            <TransferForm
              destinations={otherTeams}
              minEffectiveDate={minBulkTransferDate()}
              onSave={saveTransfer}
              onCancel={() => setTransferFormOpen(false)}
            />
          )}

          {filtered.length === 0 ? (
            <EmptyState>No personnel match your search.</EmptyState>
          ) : (
            <div className="overflow-x-auto rounded-surface border border-line">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th scope="col" className="border-b border-line bg-surface-2 px-3 py-2 text-left">
                      <span className="sr-only">Select</span>
                    </th>
                    {['Name', 'Identifier', 'Title', 'Team', 'Supervisor', 'Qualifications', 'Status'].map(
                      (heading) => (
                        <th
                          key={heading}
                          scope="col"
                          className="border-b border-line bg-surface-2 px-3 py-2 text-left text-caption font-bold tracking-wide text-ink-subtle uppercase"
                        >
                          {heading}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((person) => {
                    const { status, detailLabel } = effectiveStatusFor(person, details)
                    return (
                      <tr key={person.identifier}>
                        <td className="border-b border-line px-3 py-2">
                          <input
                            type="checkbox"
                            aria-label={`Select ${person.name}`}
                            checked={selected.has(person.id)}
                            onChange={() => toggleSelected(person.id)}
                            className="size-5"
                          />
                        </td>
                        <th scope="row" className="border-b border-line px-3 py-2 text-left text-small font-bold">
                          <Link
                            to={`/supervisor/personnel/${person.identifier}`}
                            className="rounded-control underline-offset-2 hover:underline"
                          >
                            {person.name}
                          </Link>
                        </th>
                        <td className="border-b border-line px-3 py-2 text-small text-ink-muted [font-variant-numeric:tabular-nums]">
                          {person.identifier}
                        </td>
                        <td className="border-b border-line px-3 py-2 text-small">{person.title}</td>
                        <td className="border-b border-line px-3 py-2 text-small">{person.team}</td>
                        <td className="border-b border-line px-3 py-2 text-small">{person.supervisor}</td>
                        <td className="border-b border-line px-3 py-2 text-small">
                          <div className="flex flex-wrap gap-1">
                            {person.qualifications.map((qualification) => (
                              <span
                                key={qualification}
                                className="rounded-full bg-surface-2 px-2 py-0.5 text-caption font-bold text-ink-muted"
                              >
                                {qualification}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="border-b border-line px-3 py-2 text-small">
                          <span
                            className={`rounded-full px-2 py-0.5 text-caption font-bold ${
                              status === 'detailed' ? 'bg-info/10 text-info' : 'bg-success/10 text-success'
                            }`}
                            title={detailLabel}
                          >
                            {status === 'detailed' ? 'Detailed' : 'Active'}
                          </span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
